import React from 'react';
import { Box } from '../../../CommonComponents/OPL/Box/Box';
import { Text } from '../../../CommonComponents/OPL/Text/Text';
import { Select } from '../../../CommonComponents/OPL/Select/Select/Select';
import { OptionType } from '../../../CommonComponents/OPL/Select/BaseSelect/types';
import { Icon } from '../../../CommonComponents/OPL/Icon/Icon';
import { Lozenge } from '../../../CommonComponents/OPL/Lozenge/Lozenge';
import { Tooltip } from '../../../CommonComponents/OPL/Tooltip/Tooltip';
import { formatCurrency } from '../../../CommonUtils/formatNumber';
import { DifferenceDecision, DifferenceRow as Row } from '../pdfReconciliationTypes';
import { getAcceptedSide } from '../meta';

const IMPORT_COLUMN_LABEL = 'This import';
const EXISTING_COLUMN_LABEL = 'Existing plan';

interface Props {
  row: Row;
  entitySingular: string;
  decision: DifferenceDecision | undefined;
  onChange: (decision: DifferenceDecision) => void;
}

interface ColumnHeaderProps {
  label: string;
  /**
   * Optional element rendered at the right edge of the header row, in line with the column
   * label and above the dividing border. Used to surface the source-system icon for the
   * existing-side preview without pushing the entity title or meta lines around.
   */
  trailing?: React.ReactNode;
}

const ColumnHeader = ({ label, trailing }: ColumnHeaderProps) => (
  <Box
    mb="8"
    pb="4"
    borderBottom="1px solid"
    borderColor="neutral.200"
    display="flex"
    justifyContent="space-between"
    alignItems="center"
  >
    <Text
      fontSize="small"
      fontWeight={600}
      color="neutral.700"
      letterSpacing="wide"
      textTransform="uppercase"
    >
      {label}
    </Text>
    {trailing}
  </Box>
);

const formatPreviewValue = (key: string, value: unknown): string => {
  if (value == null || value === '') {
    return '\u2014';
  }
  
  if (typeof value === 'number') {
    const lower = key.toLowerCase();
    if (
      lower.includes('amount') ||
      lower.includes('balance') ||
      lower.includes('value') ||
      lower.includes('worth')
    ) {
      return formatCurrency(value);
    }
    return value.toString();
  }
  return String(value);
};

interface PreviewBlockProps {
  label: string;
  preview: Record<string, unknown> | null;
  /**
   * Friendly upstream-system label for the existing entity (e.g. `"Linked"` / `"Integrated"`).
   * Renders the connected-account link icon next to the entity title with a tooltip
   * explaining why "Replace existing" is unavailable. Always rendered when set, regardless
   * of the row's `allowOverwrite`, so source attribution stays visible on read-only rows too.
   * The same `far fa-link` glyph is used by the Accounts page to mark connected accounts,
   * so the wizard reinforces a consistent visual language for "owned by an upstream system".
   */
  sourceLabel?: string | null;
}

const PreviewBlock = ({ label, preview, sourceLabel }: PreviewBlockProps) => {
  if (!preview) {
    return null;
  }

  const titleEntry = (preview.label as string) || '';
  const otherEntries = Object.entries(preview).filter(([k]) => k !== 'label');
  // The connected-account glyph lives in the column header's right edge (parallel to the
  // EXISTING PLAN label, above the dividing border) so it stays in a fixed top-right
  // position regardless of how the entity title below wraps.
  const headerTrailing = sourceLabel ? (
    <Tooltip
      content={`Sourced from ${sourceLabel} data. Replace is disabled to avoid overwriting authoritative upstream values.`}
    >
      <Box
        as="span"
        aria-label={`${sourceLabel} account`}
        display="inline-flex"
        alignItems="center"
        color="neutral.500"
      >
        <Icon className="far fa-link" />
      </Box>
    </Tooltip>
  ) : null;
  return (
    <Box>
      <ColumnHeader label={label} trailing={headerTrailing} />
      {!!titleEntry && (
        <Text fontWeight={600} color="neutral.700" mb="2">
          {titleEntry}
        </Text>
      )}
      {otherEntries.map(([k, v]) => (
        <Text key={k} color="neutral.600" fontSize="small">
          {k}: {formatPreviewValue(k, v)}
        </Text>
      ))}
    </Box>
  );
};

const ColumnPlaceholder = ({ columnLabel, message }: { columnLabel: string; message: string }) => (
  <Box>
    <ColumnHeader label={columnLabel} />
    <Text color="neutral.500" style={{ fontStyle: 'italic' }}>
      {message}
    </Text>
  </Box>
);

/**
 * Wraps a column with a transparent 1px border + matching padding so the layout stays stable
 * regardless of whether the column is currently the accepted side. When `isAccepted` is true
 * the border and background switch to the primary highlight palette.
 */
const ColumnHighlight = ({
  isAccepted,
  children,
}: {
  isAccepted: boolean;
  children: React.ReactNode;
}) => (
  <Box
    border="1px solid"
    borderColor={isAccepted ? 'primary.500' : 'transparent'}
    bg={isAccepted ? 'primary.100' : 'transparent'}
    borderRadius="medium"
    p="8"
  >
    {children}
  </Box>
);

interface ImportDecisionSelectProps {
  rowId: string;
  kind: Extract<Row['kind'], 'Matched' | 'ImportedOnly'>;
  entitySingular: string;
  allowOverride: boolean;
  allowOverwrite: boolean;
  value: DifferenceDecision;
  onChange: (decision: DifferenceDecision) => void;
}

const buildMatchedOptions = (
  entitySingular: string,
  allowOverride: boolean,
  allowOverwrite: boolean,
): OptionType<DifferenceDecision>[] => {
  const options: OptionType<DifferenceDecision>[] = [
    {
      value: 'Discard',
      label: `Keep existing ${entitySingular} and discard imported ${entitySingular}.`,
    },
  ];
  if (allowOverwrite) {
    options.push({
      value: 'Overwrite',
      label: `Replace existing ${entitySingular} with imported ${entitySingular}`,
    });
  }
  if (allowOverride) {
    options.push({
      value: 'Add',
      label: `Add imported ${entitySingular} as new`,
    });
  }
  return options;
};

const ImportDecisionSelect = ({
  rowId,
  kind,
  entitySingular,
  allowOverride,
  allowOverwrite,
  value,
  onChange,
}: ImportDecisionSelectProps) => {
  const options: OptionType<DifferenceDecision>[] =
    kind === 'Matched'
      ? buildMatchedOptions(entitySingular, allowOverride, allowOverwrite)
      : [
          { value: 'Add', label: `Add imported ${entitySingular} to existing plan` },
          { value: 'Discard', label: `Discard imported ${entitySingular}` },
        ];

  return (
    <Select
      id={`${rowId}-decision`}
      aria-label={
        kind === 'Matched'
          ? `Choose action for matching ${entitySingular}`
          : `Choose action for new ${entitySingular}`
      }
      isPortal
      value={options.find(o => o.value === value)}
      options={options}
      onChange={opt => {
        if (!opt) return;
        onChange(opt.value);
      }}
    />
  );
};

const getMatchedBadge = (
  matchConfidence: string | null,
): { label: string; variantColor: 'success' | 'warning' | 'default' } => {
  if (matchConfidence === 'Medium') {
    return { label: 'Possible match', variantColor: 'warning' };
  }
  if (matchConfidence === 'High' || matchConfidence === 'Deterministic') {
    return { label: 'Matching item', variantColor: 'success' };
  }
  // Matched row with missing or legacy confidence — show as possible match.
  return { label: 'Possible match', variantColor: 'warning' };
};

const RowStatusLozenge = ({ row }: { row: Row }) => {
  if (row.kind === 'ImportedOnly') {
    return <Lozenge variantColor="new">New item</Lozenge>;
  }

  if (row.kind === 'ExistingOnly') {
    return <Lozenge variantColor="default">Not in PDF</Lozenge>;
  }

  if (row.kind === 'Matched') {
    const { label, variantColor } = getMatchedBadge(row.matchConfidence);
    const lozenge = <Lozenge variantColor={variantColor}>{label}</Lozenge>;
    return row.matchRationale ? <Tooltip content={row.matchRationale}>{lozenge}</Tooltip> : lozenge;
  }

  return null;
};

const renderImportColumn = (row: Row) => {
  if (row.kind === 'Matched' || row.kind === 'ImportedOnly') {
    return <PreviewBlock label={IMPORT_COLUMN_LABEL} preview={row.importedPreview} />;
  }

  if (row.kind === 'ExistingOnly') {
    return <ColumnPlaceholder columnLabel={IMPORT_COLUMN_LABEL} message="Not found in PDF" />;
  }

  return null;
};

const renderExistingColumn = (row: Row) => {
  if (row.kind === 'Matched' || row.kind === 'ExistingOnly') {
    return (
      <PreviewBlock
        label={EXISTING_COLUMN_LABEL}
        preview={row.existingPreview}
        sourceLabel={row.existingSourceLabel}
      />
    );
  }

  if (row.kind === 'ImportedOnly') {
    return <ColumnPlaceholder columnLabel={EXISTING_COLUMN_LABEL} message="Not in existing plan" />;
  }

  return null;
};

/**
 * Returns the decision dropdown for an actionable row, or null when the row is read-only.
 * Rendered below the comparison grid (not inside either column) so it visually pairs with
 * both sides while letting the accepted-column highlight track its current selection.
 */
const renderActionSelect = (
  row: Row,
  entitySingular: string,
  decision: DifferenceDecision,
  onChange: (decision: DifferenceDecision) => void,
) => {
  if (row.kind === 'Matched' && (row.allowOverride || row.allowOverwrite)) {
    return (
      <ImportDecisionSelect
        rowId={row.rowId}
        kind="Matched"
        entitySingular={entitySingular}
        allowOverride={row.allowOverride}
        allowOverwrite={row.allowOverwrite}
        value={decision}
        onChange={onChange}
      />
    );
  }

  if (row.kind === 'ImportedOnly') {
    return (
      <ImportDecisionSelect
        rowId={row.rowId}
        kind="ImportedOnly"
        entitySingular={entitySingular}
        allowOverride={row.allowOverride}
        allowOverwrite={row.allowOverwrite}
        value={decision}
        onChange={onChange}
      />
    );
  }

  return null;
};

/**
 * Renders one reconciliation row. Import is always left, existing plan right.
 * - ImportedOnly: action select (default "Add {entity} to existing plan").
 * - Matched with allowOverride and/or allowOverwrite: action select with a dynamic option
 *   set built from the row flags — "Keep existing" is always present, "Replace existing"
 *   when allowOverwrite, "Add as new" when allowOverride.
 * - Matched rows with neither flag (legacy read-only) and ExistingOnly rows have no
 *   action control.
 *
 * The column corresponding to the advisor's current decision (left for Add/Overwrite,
 * right for Discard) is highlighted with a primary border + tint so the accepted side
 * is obvious at a glance.
 */
export const DifferenceRow = ({ row, entitySingular, decision, onChange }: Props) => {
  const effectiveDecision = decision ?? row.defaultDecision;
  const acceptedSide = getAcceptedSide(row, effectiveDecision);
  const actionSelect = renderActionSelect(row, entitySingular, effectiveDecision, onChange);

  return (
    <Box border="1px solid" borderColor="neutral.200" borderRadius="medium" p="16" mb="8">
      <Box display="flex" alignItems="flex-start" css={({ theme }) => ({ gap: theme.space[12] })}>
        <Box flex={1} minWidth={0}>
          <Box
            display="grid"
            css={({ theme }) => ({
              gridTemplateColumns: '1fr 1fr',
              gap: theme.space[24],
            })}
          >
            <ColumnHighlight isAccepted={acceptedSide === 'import'}>
              {renderImportColumn(row)}
            </ColumnHighlight>
            <ColumnHighlight isAccepted={acceptedSide === 'existing'}>
              {renderExistingColumn(row)}
            </ColumnHighlight>
          </Box>
          {actionSelect && <Box mt="12">{actionSelect}</Box>}
        </Box>
        <Box
          flexShrink={0}
          width="11.5rem"
          display="flex"
          justifyContent="flex-end"
          alignItems="flex-start"
        >
          <RowStatusLozenge row={row} />
        </Box>
      </Box>
    </Box>
  );
};
